import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

// L14.04 — Commercial Disputes (read-only).
// As-built note: there is no dedicated `commercial_disputes` table and the
// M14ReviewDecision enum has no DISPUTE value. A commercial disagreement is
// recorded as a non-approve review outcome (RETURNED / REJECTED / ESCALATED /
// HOLD). This screen surfaces those disputed reviews from the commercial
// reviews (`/v1/approval/dashboard/reviews`); resolution happens via the claim
// review / correction-request flow.

interface QueueItem {
  readonly reviewId: string;
  readonly claimId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly reviewStatus: string;
  readonly createdAt: string;
}

const DISPUTE_STATUSES = new Set(['RETURNED', 'REJECTED', 'ESCALATED', 'HOLD', 'DISPUTED', 'RETURN', 'ESCALATE']);

const STATUS_FILTERS = ['ALL', 'RETURNED', 'REJECTED', 'ESCALATED', 'HOLD'];

export function CommercialDisputesPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at, day } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<QueueItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const q = await apiRequest<QueueItem[]>('/v1/approval/dashboard/reviews', { token });
      const disputed = (Array.isArray(q) ? q : []).filter((r) => DISPUTE_STATUSES.has(r.reviewStatus));
      setRows(statusFilter === 'ALL' ? disputed : disputed.filter((r) => r.reviewStatus === statusFilter));
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="commercial-disputes">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/financial-approval" data-testid="back-dashboard">{t('fa.disputes.backDashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('fa.disputes.title')}</h1>
      </div>

      <p style={{ color: 'var(--color-text-muted, #656d76)', marginBottom: 12, fontSize: 13 }}>
        {t('fa.disputes.intro')}
      </p>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="dispute-status" style={{ fontSize: 12, marginRight: 8 }}>{t('fa.disputes.statusLabel')}</label>
        <select id="dispute-status" data-testid="dispute-status-filter" value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {STATUS_FILTERS.map((s) => <option key={s} value={s}>{t(`fa.disputes.filter.${s}`)}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      {rows.length === 0 ? (
        <p data-testid="disputes-empty" style={{ color: 'var(--color-text-muted, #656d76)' }}>{t('fa.disputes.empty')}</p>
      ) : (
        <table aria-label="disputes-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>{t('fa.disputes.colClaim')}</th>
              <th style={{ padding: '8px 4px' }}>{t('fa.disputes.colVendor')}</th>
              <th style={{ padding: '8px 4px' }}>{t('fa.disputes.colStatus')}</th>
              <th style={{ padding: '8px 4px' }}>{t('fa.disputes.colRaised')}</th>
              <th style={{ padding: '8px 4px' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reviewId} data-testid={`dispute-row-${r.reviewId}`}
                  style={{ borderBottom: '1px solid var(--color-border-default, #d0d7de)' }}>
                <td style={{ padding: '8px 4px' }}>
                  <Link to={`/financial-approval/claim/${r.claimId}`}>{billRefLabel(r.bill, day)}</Link>
                </td>
                <td style={{ padding: '8px 4px' }}>{personLabel(r.vendorName, r.vendorCode) ?? '—'}</td>
                <td style={{ padding: '8px 4px', color: r.reviewStatus === 'REJECTED' ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                  {r.reviewStatus}
                </td>
                <td style={{ padding: '8px 4px', fontSize: 12 }}>{at(r.createdAt)}</td>
                <td style={{ padding: '8px 4px' }}>
                  <Link to={`/financial-approval/claim/${r.claimId}/corrections`}
                        data-testid={`dispute-correct-${r.reviewId}`}>{t('fa.disputes.corrections')}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
